import { useState, useMemo, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
  type SortingStrategy,
} from "@dnd-kit/sortable";
import { Box } from "@radix-ui/themes";

interface SortableListProps<T> {
  items: T[];
  getItemId: (item: T) => string | number;
  onReorder: (reorderedItems: T[]) => void;
  renderItem: (item: T) => React.ReactNode;
  renderDragOverlay?: (item: T) => React.ReactNode;
  strategy?: SortingStrategy;
  containerStyle?: React.CSSProperties;
}

export default function SortableList<T>({
  items,
  getItemId,
  onReorder,
  renderItem,
  renderDragOverlay,
  strategy = verticalListSortingStrategy,
  containerStyle,
}: SortableListProps<T>) {
  const [activeItem, setActiveItem] = useState<T | null>(null);
  const [internalItems, setInternalItems] = useState(items);
  const isReordering = useRef(false);

  // Sync from parent — but skip if we just reordered internally
  useEffect(() => {
    if (!isReordering.current) {
      setInternalItems(items);
    }
    isReordering.current = false;
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const itemIds = useMemo(
    () => internalItems.map((item) => getItemId(item)),
    [internalItems, getItemId],
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveItem(
      internalItems.find((item) => getItemId(item) === event.active.id) ?? null,
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItem(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = internalItems.findIndex(
      (item) => getItemId(item) === active.id,
    );
    const newIndex = internalItems.findIndex(
      (item) => getItemId(item) === over.id,
    );
    const reordered = arrayMove(internalItems, oldIndex, newIndex);

    // Update internal state immediately — no flash
    isReordering.current = true;
    setInternalItems(reordered);
    onReorder(reordered);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={strategy}>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-1)",
            ...containerStyle,
          }}
        >
          {internalItems.map((item) => renderItem(item))}
        </Box>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeItem && renderDragOverlay ? (
          <Box
            style={{
              transform: "rotate(2deg)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              borderRadius: "var(--radius-3)",
              backgroundColor: "var(--color-background)",
            }}
          >
            {renderDragOverlay(activeItem)}
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
